import { Suspense, lazy, useContext } from "react";
import { Route, Routes } from "react-router";
import { Box, Button, Spinner } from "@cloudscape-design/components";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "react-i18next";

import CommonAlert from "./comps/alert";
import ReSignIn from "./comps/ReSignIn";
import LoginCallback from "./comps/LoginCallback";
import ConfigContext from "@/context/config-context";
import { LAST_VISIT_URL } from "@/utils/const";

const NotFound = lazy(() => import("./pages/common/NotFound"));
const ContextDetail = lazy(() => import("./pages/context/ContextDetail"));
const DocumentDetail = lazy(() => import("./pages/context/DocumentDetail"));
const GroupDetail = lazy(() => import("./pages/context/GroupDetail"));
const GeneralSettings = lazy(() => import("./pages/settings/GeneralSettings"));
const ApiKeys = lazy(() => import("./pages/settings/ApiKeys"));
const GraphConnections = lazy(() => import("./pages/graph-connections/GraphConnections"));
const Context = lazy(() => import("./pages/context/Context"));
const ChatBox = lazy(() => import("./pages/chat/ChatBox"));
const VisualizeGraph = lazy(() => import("./pages/graph/VisualizeGraph"));
const GenContent = lazy(() => import("./pages/gen-content/GenContent"));
const ContextCreate = lazy(() => import("./pages/context/ContextCreate"));
const CitationViewerPage = lazy(() => import("./pages/citation-viewer/CitationViewerPage").then(m => ({ default: m.CitationViewerPage })));
const ShellPage = lazy(() => import("./pages/shell/ShellPage"));
const PopupCallback = lazy(() => import("./pages/shell/PopupCallback"));
const NeptuneQueryPage = lazy(() => import("./pages/neptune-query/NeptuneQueryPage"));
const OpenSearchQueryPage = lazy(() => import("./pages/opensearch-query/OpenSearchQueryPage"));
const DebugConfigPage = lazy(() => import("./pages/debug-config/DebugConfigPage"));
const ModelsPage = lazy(() => import("./pages/models/ModelsPage"));
const ModelCreate = lazy(() => import("./pages/models/ModelCreate"));
const PromptTemplateList = lazy(() => import("./pages/prompt-template/PromptTemplateList"));
const PromptTemplateCreate = lazy(() => import("./pages/prompt-template/PromptTemplateCreate"));
const PromptTemplateDetail = lazy(() => import("./pages/prompt-template/PromptTemplateDetail"));
const ModelEdit = lazy(() => import("./pages/models/ModelEdit"));
const AgentPage = lazy(() => import("./pages/agent/AgentPage"));
const OntologyPage = lazy(() => import("./pages/ontology/OntologyPage"));

const LoadingFallback = () => (
  <div className="page-loading">
    <Spinner />
  </div>
);

const PublicRouter = () => {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/shell" element={<ShellPage />} />
        <Route path="/popup-callback" element={<PopupCallback />} />
        <Route path="/signin" element={<LoginCallback />} />
        <Route path="/citation-viewer" element={<CitationViewerPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const SignedInRouter = () => {
  return (
    <>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/signin" element={<LoginCallback />} />
          <Route path="/" element={<Context />} />
          <Route path="/context/create" element={<ContextCreate />} />
          <Route path="/context/detail/:id" element={<ContextDetail />} />
          <Route path="/context/detail/:contextId/document/:s3Key" element={<DocumentDetail />} />
          <Route path="/context/detail/:contextId/group/:groupId" element={<GroupDetail />} />
          <Route path="/connections" element={<GraphConnections />} />
          <Route path="/chat" element={<ChatBox />} />
          <Route path="/visualize" element={<VisualizeGraph />} />
          <Route path="/generated-contents" element={<GenContent />} />
          <Route path="/settings/general" element={<GeneralSettings />} />
          <Route path="/settings/api-keys" element={<ApiKeys />} />
          <Route path="/citation-viewer" element={<CitationViewerPage />} />
          <Route path="/neptune-query" element={<NeptuneQueryPage />} />
          <Route path="/opensearch-query" element={<OpenSearchQueryPage />} />
          <Route path="/debug-config" element={<DebugConfigPage />} />
          <Route path="/models" element={<ModelsPage />} />
          <Route path="/models/create" element={<ModelCreate />} />
          <Route path="/models/:configId/edit" element={<ModelEdit />} />
          <Route path="/prompt-templates" element={<PromptTemplateList />} />
          <Route path="/prompt-templates/create" element={<PromptTemplateCreate />} />
          <Route path="/prompt-templates/:templateId" element={<PromptTemplateDetail />} />
          <Route path="/agent" element={<AgentPage />} />
          <Route path="/ontology" element={<OntologyPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <CommonAlert />
    </>
  );
};

const AppRouter = () => {
  const auth = useAuth();
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const publicUrl = process.env.PUBLIC_URL ?? "";
  const basePath = publicUrl.endsWith("/") ? publicUrl.slice(0, -1) : publicUrl;
  const rawPath = window.location.pathname;
  const appPath =
    basePath && rawPath.startsWith(basePath)
      ? rawPath.slice(basePath.length) || "/"
      : rawPath;
  const appLocation = `${appPath}${window.location.search}${window.location.hash}`;

  const citationViewerIsPublic =
    appPath === "/citation-viewer" &&
    config?.enable_citation_viewer_auth === false;
  const isPublicRoute =
    appPath === "/shell" ||
    appPath === "/popup-callback" ||
    citationViewerIsPublic;

  if (auth.isLoading) {
    return (
      <div className="page-loading">
        <Spinner />
      </div>
    );
  }

  if (isPublicRoute) {
    return <PublicRouter />;
  }

  if ((!auth.isAuthenticated && auth.user) || auth.error) {
    return (
      <>
        <ReSignIn />
        <SignedInRouter />
      </>
    );
  }

  if (auth.isAuthenticated) {
    return <SignedInRouter />;
  }
  return (
    <div className="login-container">
      <div className="text-center">
        <Box variant="h2">{t("welcome")}</Box>
        <div className="mt-10">
          <Button
            variant="primary"
            onClick={() => {
              localStorage.setItem(LAST_VISIT_URL, appLocation);
              void auth.signinRedirect();
            }}
          >
            {t("button.login")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AppRouter;
